import preval from 'babel-plugin-preval/macro';
import {
  LOADED_SUBSCRIPTIONS,
  LOADING_SUBSCRIPTIONS,
  SubscriptionActionTypes,
  SubscriptionState,
  LOADED_USER_SUBSCRIPTIONS,
  SubscriptionLoadingStatus,
} from './types';

const initialState: SubscriptionState = /*preval*/ {
  ...preval`
  const subData = require('../../data/allsubscriptions.json')
  module.exports = subData;
  `,
  loadState: SubscriptionLoadingStatus.Initial,
  userDataLoaded: false,
  globalDataLoaded: false,
};

interface IHasUUid {
  uuid: string;
}
const reconcileUserData = <T extends IHasUUid>(
  originalVals: T[],
  updatedVals: T[],
  globalDataLoaded: boolean
): T[] => {
  const groupings = Object.assign(
    {},
    ...updatedVals.map((x) => ({ [x.uuid]: { curr: x } }))
  );

  originalVals.forEach((x) => {
    if (typeof groupings[x.uuid] !== 'undefined') {
      groupings[x.uuid].old = x;
    } else {
      groupings[x.uuid] = {
        old: x,
      };
    }
  });

  const reconciledVals = Object.keys(groupings).map((uuid) => {
    const { curr, old } = groupings[uuid];
    if (typeof old === 'undefined') {
      return curr;
    }

    if (typeof curr !== 'undefined') {
      return curr;
    }
    return {
      ...old,
      userHasAccess: false,
      showInMyEcri: false,
    };
  });

  return reconciledVals.filter((x) => !!x);
};

const reducer = (
  state = initialState,
  action: SubscriptionActionTypes
): SubscriptionState => {
  switch (action.type) {
    case LOADING_SUBSCRIPTIONS: {
      return {
        ...state,
        loadState: SubscriptionLoadingStatus.Loading,
      };
    }
    case LOADED_SUBSCRIPTIONS: {
      if (state.userDataLoaded) {
        const currentComponentsById = Object.assign(
          {},
          ...state.allComponents.map((x) => ({
            [x.uuid]: x,
          }))
        );

        const currentMembershipsById = Object.assign(
          {},
          ...state.allMemberships.map((x) => ({
            [x.uuid]: x,
          }))
        );

        return {
          allComponents: state.allComponents.map((x) => {
            const updatedEntry = currentComponentsById[x.uuid];
            if (typeof updatedEntry === 'undefined') {
              return x;
            }
            const merged = {
              ...updatedEntry,
              userHasAccess: x.userHasAccess,
            };
            return merged;
          }),
          allMemberships: state.allMemberships.map((x) => {
            const updatedEntry = currentMembershipsById[x.uuid];
            if (typeof updatedEntry === 'undefined') {
              return x;
            }
            const merged = {
              ...updatedEntry,
              userHasAccess: x.userHasAccess,
            };
            return merged;
          }),
          allComponentTypes: action.payload.allComponentTypes,
          allMembershipCategories: action.payload.allMembershipCategories,
          loadState: SubscriptionLoadingStatus.Loaded,
          userDataLoaded: true,
          globalDataLoaded: true,
        };
      }
      return {
        ...action.payload,
        loadState: SubscriptionLoadingStatus.Loaded,
        userDataLoaded: false,
        globalDataLoaded: true,
      };
    }
    case LOADED_USER_SUBSCRIPTIONS: {
      return {
        ...state,
        allComponents: reconcileUserData(
          state.allComponents,
          action.payload.allComponents,
          state.userDataLoaded
        ),
        allMemberships: reconcileUserData(
          state.allMemberships,
          action.payload.allMemberships,
          state.userDataLoaded
        ),
        loadState: SubscriptionLoadingStatus.Loaded,
        userDataLoaded: true,
      };
    }
    case 'SAVING_USER_PREFERENCES': {
      return {
        ...state,
        loadState: SubscriptionLoadingStatus.Updating,
      };
    }
    case 'SAVED_USER_PREFERENCES': {
      const updatesKeyed = Object.assign(
        {},
        ...action.payload.map((x) => ({ [x.uuid]: x.showInMyEcri }))
      );
      return {
        ...state,
        allComponents: state.allComponents.map((x) => {
          const updatedDisplayValue = updatesKeyed[x.uuid];
          if (typeof updatedDisplayValue !== 'undefined') {
            return {
              ...x,
              showInMyEcri: updatedDisplayValue,
            };
          } else {
            return x;
          }
        }),
        loadState: SubscriptionLoadingStatus.Loaded,
      };
    }
    case 'SAVING_USER_PREFERENCES_ERROR': {
      return {
        ...state,
        loadState: SubscriptionLoadingStatus.Error,
      };
    }
    default: {
      return state;
    }
  }
};

export default reducer;
